import { ReactNode } from 'react';
import ReactQuill, { ReactQuillProps } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import useRequiredId from 'hooks/useRequiredId';
import Tooltip from 'components/Tooltip/Tooltip';
import { HelpIcon } from 'components/SvgIcons';

import './RichTextAreaInput.scss';

type Variant = 'standard' | 'filled';

export interface RichTextAreaInputProps extends ReactQuillProps {
  label?: string;
  error?: string | ReactNode;
  hint?: string | ReactNode;
  touched?: boolean;
  fullWidth?: boolean;
  rows?: number;
  variant?: Variant;
  required?: boolean;
  disabled?: boolean;
}

const RichTextAreaInput = ({
  id,
  label,
  error,
  touched = false,
  required = false,
  fullWidth = false,
  className = '',
  hint,
  rows,
  variant = 'standard',
  disabled = false,
  ...props
}: RichTextAreaInputProps) => {
  const ensuredId = useRequiredId(id);
  const errorId = `${ensuredId}-error`;

  const hasError = Boolean(error && error !== '' && touched);

  return (
    <div className={`enkrateia-rich-textarea-input ${className} variant-${variant} ${fullWidth ? 'full-width' : ''}`}>
      {label && (
        <label htmlFor={ensuredId}>
          {label}
          {required && '*'}
          {hint && (
            <Tooltip tooltip={hint}>
              <HelpIcon color="#2D2C64" size={25} />
            </Tooltip>
          )}
        </label>
      )}
      <div
        className={`quill-container ${disabled ? 'disabled' : ''}`}
        style={{ minHeight: rows ? `${rows * 30}px` : 'auto' }}
      >
        <ReactQuill
          {...props}
          readOnly={disabled}
          theme="snow"
          aria-invalid={hasError}
          aria-errormessage={errorId}
          id={ensuredId}
        />
      </div>
      {hasError && (
        <span id={errorId} className="error-label">
          {error}
        </span>
      )}
    </div>
  );
};

export default RichTextAreaInput;
